// ariaVoiceHealth.js — WHY SHE WENT SILENT, AND HOW TO FIX IT (Braeden 8/15)
//
//   "they could say yes (easier but if they say no) think of every reson they
//    might not be able to hear the ai, than you have to fiind the solustion
//    and fix it"
//
// "Can you hear me okay?" -> "no" was answered with "No problem, we can do this
// by text." That is not a fix, it is a surrender: it throws away her voice on
// the first turn and never asks WHY. And in most cases the why is OURS.
//
// speakAriaLine had FIVE separate silent returns - a bad HTTP status, an empty
// buffer, a decode error, a blocked play(), and a catch-all - and every one of
// them left her believing she had spoken. So she asked "can you hear me?", the
// person heard nothing, and she had no idea anything had gone wrong.
//
// This module makes the failure KNOWN. Every speech attempt records what
// happened; when they say they cannot hear her, she reads the last failure and
// answers the actual cause instead of guessing.
//
// THE BIG ONE IS AUTOPLAY. Browsers refuse to play sound until the user has
// interacted with the page. On first run she tries to speak IMMEDIATELY, which
// is the one moment no interaction has happened yet - so the very first thing
// she ever says is the most likely thing to be silently swallowed. The saving
// grace: the tap or keystroke they use to answer "no" IS the gesture that
// unlocks audio. So by the time she responds, she can genuinely replay it.

// ── what we know about the last attempt ───────────────────────────────────
const state = {
  attempts: 0,
  played: 0,          // clips that actually reached onplay
  lastFailure: null,  // one of the CAUSE ids below
  lastError: '',
  lastAt: 0,
  everPlayed: false,  // has ANY clip ever really played in this session
};

export const CAUSE = {
  BLOCKED: 'autoplay_blocked',   // browser refused until a user gesture
  HTTP: 'tts_http',              // /tts returned a bad status
  EMPTY: 'tts_empty',            // 200 but zero bytes
  DECODE: 'decode_error',        // got bytes, could not play them
  UNKNOWN: 'unknown',
};

export function noteAttempt() { state.attempts++; state.lastAt = Date.now(); }
export function notePlayed() {
  state.played++; state.everPlayed = true;
  state.lastFailure = null; state.lastError = '';
}
export function noteFailure(cause, err) {
  state.lastFailure = cause || CAUSE.UNKNOWN;
  state.lastError = String((err && err.message) || err || '');
  state.lastAt = Date.now();
}
export function voiceState() { return { ...state }; }

/** A play() rejection tells us WHICH failure it was — the name is the signal. */
export function causeFromPlayError(err) {
  const name = String((err && err.name) || '');
  const msg = String((err && err.message) || '');
  if (name === 'NotAllowedError' || /gesture|interact|user activation/i.test(msg)) return CAUSE.BLOCKED;
  if (name === 'NotSupportedError' || /decode|format|source/i.test(msg)) return CAUSE.DECODE;
  return CAUSE.UNKNOWN;
}

// ── EVERY REASON A PERSON CANNOT HEAR HER ─────────────────────────────────
//
// Split by who can fix it, because that decides whether she acts or instructs.
// OURS   -> she repairs it herself and proves it by speaking again.
// THEIRS -> she names the one thing to check. One thing, not a checklist:
//           a person who already cannot hear you will not follow six steps.
// NEITHER-> the environment or the person. She stops trying to fix and adapts.
//
// Ordered by how often it is actually the cause on a first run, because she
// gets ONE guess before this stops feeling like help.
export const DEAF_CAUSES = [
  {
    id: 'autoplay_blocked',
    owner: 'ours',
    // The `!everPlayed` fallback is a GUESS, so it must only apply when nothing
    // more specific is known. Without that guard it swallowed every other
    // cause: a dead TTS server still got "your browser blocked me", which sends
    // the person hunting through their own settings for our outage (8/15).
    when: (v) => v.lastFailure === CAUSE.BLOCKED || (!v.lastFailure && !v.everPlayed && v.attempts > 0),
    // She does not ask them to fix anything. Their tap already fixed it.
    say: "That's on me — your browser held my sound. Listen again.",
    action: 'replayGreeting',
  },
  {
    id: 'tts_down',
    owner: 'ours',
    when: (v) => v.lastFailure === CAUSE.HTTP || v.lastFailure === CAUSE.EMPTY,
    say: "That's my end, not yours. One second.",
    action: 'retryVoice',
  },
  {
    id: 'decode',
    owner: 'ours',
    when: (v) => v.lastFailure === CAUSE.DECODE,
    say: "That came through broken. Sending it again.",
    action: 'fallbackVoice',
  },
  {
    id: 'tab_muted',
    owner: 'theirs',
    when: () => true,
    // Chrome puts a crossed-out speaker on a muted tab and people never look.
    say: "Check the speaker icon on this tab. Usually that.",
    action: 'replayGreeting',
  },
  {
    id: 'volume_down',
    owner: 'theirs',
    when: () => true,
    say: "Have a look at your volume.",
    action: 'replayGreeting',
  },
  {
    id: 'wrong_output',
    owner: 'theirs',
    when: () => true,
    // Bluetooth buds still paired to a phone in a pocket is extremely common
    // on a jobsite, and nothing on screen tells them.
    say: "Might be going to headphones or a monitor.",
    action: 'replayGreeting',
  },
];

/**
 * What she should say and do when they cannot hear her.
 * `tries` = how many times she has already tried to fix it this conversation.
 * Each try moves to the next cause, so she never repeats a failed suggestion.
 */
export function fixForCannotHear(tries) {
  const v = voiceState();
  const applicable = DEAF_CAUSES.filter((c) => c.when(v));
  // She has already been wrong `tries` times — move down the list.
  const n = tries || 0;
  // >= length, not length-1. The old bound retired the list one cause early, so
  // "sound might be going somewhere else" — bluetooth buds still paired to a
  // phone, the single most common cause on a jobsite — was never once said.
  if (n >= applicable.length) {
    // SHE STOPS FIGHTING IT. Three failed guesses is where insisting on voice
    // becomes worse than dropping it, and text is a real product, not a
    // consolation. Only here does "we'll do this by text" become the honest
    // answer instead of the lazy one.
    return {
      id: 'give_up_gracefully',
      say: "No luck — we'll do it by text then, works just as well. I'll keep typing.",
      action: 'textOnly',
    };
  }
  const pick = applicable[n];
  return { id: pick.id, say: pick.say, action: pick.action, owner: pick.owner };
}

// ── "I can hear you but..." — NOT the same problem ────────────────────────
//
// A person who says "you're too quiet", "slow down", "you cut out" CAN hear
// her. Treating those as deafness and launching into browser-tab advice is
// how she loses someone who was actually trying to help her. Each of these has
// its own repair, and every one of them is ours.
export const HEARD_BUT = [
  { id: 'too_quiet', test: /\b(too quiet|quiet|louder|speak up|barely hear|hard to hear|low volume|turn.*up)\b/i,
    say: "I'll speak up.", action: 'louder' },
  // NOT a bare \bloud\b. "loud and clear" is the most enthusiastic YES a person
  // can give, and it was being read as a complaint that she was too loud — so
  // the one person who was delighted got an apology and a volume drop (8/15).
  { id: 'too_loud', test: /\b(too loud|so loud|way loud|blast|deafen|turn (it|that|you) down|turn down|quieter|lower the volume)\b/i,
    say: "Sorry — bringing it down.", action: 'quieter' },
  { id: 'too_fast', test: /\b(too fast|slow down|slower|talking fast|speeding)\b/i,
    say: "I'll slow down.", action: 'slower' },
  { id: 'too_slow', test: /\b(too slow|speed up|faster|drag)\b/i,
    say: "I'll pick it up.", action: 'faster' },
  { id: 'cut_out', test: /\b(cut(ting)? out|chopp|broke up|breaking up|glitch|stutter|robotic|garbled)\b/i,
    say: "That's my end. Let me send it cleaner.", action: 'fallbackVoice' },
  { id: 'one_ear', test: /\b(one ear|left ear|right ear|one side|only one)\b/i,
    say: "That'll be your headphones, not me — but I'll keep it centred.", action: 'replayGreeting' },
  { id: 'echo', test: /\b(echo|feedback|doubl|hearing myself)\b/i,
    say: "Echo usually means my sound is going back into your mic. Headphones kill it.", action: null },
  { id: 'accent', test: /\b(accent|understand you|mumbl|unclear|slur|enunciat)\b/i,
    say: "I'll be clearer.", action: 'slower' },
];

export function matchHeardBut(text) {
  const t = String(text || '').trim();
  if (!t) return null;
  for (const h of HEARD_BUT) if (h.test.test(t)) return h;
  return null;
}

// ── CANNOT hear vs DID NOT catch it ───────────────────────────────────────
// "what?" is not "I can't hear you" — it is "say it again". Sending someone who
// simply missed a word into an audio troubleshoot is infuriating, and the two
// are one word apart in how people phrase them.
// "audio isnt working" read as nothing at all, because only "not working" was
// listed and nobody types the space. Contractions are how people actually type.
const REALLY_DEAF = /\b(can'?t hear|cannot hear|no sound|nothing|not hearing|hear nothing|silent|silence|mute[d]?|dead|nada|no audio|(is|are|does|do)n'?t work(ing|s)?|ain'?t work(ing|s)?|won'?t work|not work(ing|s)?|no voice|don'?t hear|didn'?t hear)\b/i;
const JUST_MISSED = /^(what|huh|sorry|pardon|come again|say (that )?again|repeat)/i;

// A bare "no" is the single most common answer to "can you hear me okay?" and
// it read as NOTHING here, so it fell straight past the repair and into the old
// give-up line. Context decides: at the greeting a plain no means "no, I cannot
// hear you"; anywhere else it is just a no. (8/15)
const BARE_NO = /^(n|no|nope|nah|negative|uh uh|not really)[\s.!]*$/i;

export function readHearingAnswer(text, o = {}) {
  const t = String(text || '').trim();
  if (!t) return null;
  if (JUST_MISSED.test(t) && !REALLY_DEAF.test(t)) return 'missed';
  if (REALLY_DEAF.test(t)) return 'deaf';
  const hb = matchHeardBut(t);
  if (hb) return 'heard_but';
  if (o.atGreeting && BARE_NO.test(t)) return 'deaf';
  return null;
}
